import React, { useMemo, useState } from 'react'
import { Button, Checkbox, Input } from '@surecompanies/core_components'
import { Radio } from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import isNil from 'lodash/isNil'
import {
  CustomerOption,
  type UserListStats,
  type UserStatusBucket,
} from '../../api/dto/user-invitation.dto'
import {
  CompactFilterPanel,
  FilterEmptyText,
  FilterOptionList,
  FilterPanelFooter,
  HeaderFilterPanel,
  ResetLink,
} from './UserManagement.styles'
import { STATUS_FILTER_OPTIONS } from './userDisplay'

interface AccessFilterPanelProps {
  /** True keeps the Yes rows, false the No rows, null leaves them all. */
  value: boolean | null
  onChange: (next: boolean | null) => void
}

/**
 * Yes/No filter for an access column. The two options are exhaustive, so
 * picking one replaces the other and clearing it drops the filter entirely.
 */
const AccessFilterPanel = ({ value, onChange }: AccessFilterPanelProps) => {
  // Radios, not checkboxes: the answers are exclusive, and a tick box next to
  // each would suggest both could be picked. Reset is what clears the choice.
  return (
    <CompactFilterPanel>
      <FilterOptionList>
        <Radio.Group
          value={value}
          onChange={(event) => onChange(event.target.value as boolean)}
        >
          <Radio value>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </FilterOptionList>
      <FilterPanelFooter>
        <ResetLink
          type="button"
          onClick={() => onChange(null)}
          disabled={isNil(value)}
        >
          Reset
        </ResetLink>
      </FilterPanelFooter>
    </CompactFilterPanel>
  )
}

interface StatusFilterPanelProps {
  /** Ticked buckets; a user is kept when they match any of them. */
  value: UserStatusBucket[]
  /** Per-option counts; null until the first load lands. */
  stats: UserListStats | null
  onChange: (next: UserStatusBucket[]) => void
}

/**
 * Where a user stopped short of the role. Tick as many as needed: the options
 * do not overlap, so the rows shown are the ticked counts added up.
 */
const StatusFilterPanel = ({
  value,
  stats,
  onChange,
}: StatusFilterPanelProps) => {
  const selected = useMemo(() => new Set(value), [value])

  const handleToggle =
    (status: UserStatusBucket) => (event: CheckboxChangeEvent) =>
      onChange(
        event.target.checked
          ? [...value, status]
          : value.filter((bucket) => bucket !== status)
      )

  return (
    <CompactFilterPanel>
      <FilterOptionList>
        {STATUS_FILTER_OPTIONS.map(({ label, value: status, statKey }) => {
          const count = stats?.[statKey]
          return (
            <Checkbox
              key={status}
              checked={selected.has(status)}
              onChange={handleToggle(status)}
            >
              {isNil(count) ? label : `${label} (${count})`}
            </Checkbox>
          )
        })}
      </FilterOptionList>
      <FilterPanelFooter>
        <ResetLink
          type="button"
          onClick={() => onChange([])}
          disabled={value.length === 0}
        >
          Reset
        </ResetLink>
      </FilterPanelFooter>
    </CompactFilterPanel>
  )
}

interface CompanyFilterPanelProps {
  options: CustomerOption[]
  /** True while the company list is still being fetched. */
  isLoading: boolean
  selectedKeys: string[]
  onChange: (next: string[]) => void
}

/**
 * Company filter: search, tick as many as needed. "Select all" covers the
 * companies currently listed, so it means what it says while a search narrows
 * the list.
 */
const CompanyFilterPanel = ({
  options,
  isLoading,
  selectedKeys,
  onChange,
}: CompanyFilterPanelProps) => {
  const [search, setSearch] = useState('')

  const visibleOptions = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (term.length === 0) return options
    return options.filter((option) => option.name.toLowerCase().includes(term))
  }, [options, search])

  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys])
  const visibleSelectedCount = visibleOptions.filter((option) =>
    selected.has(option.id)
  ).length
  const areAllVisibleSelected =
    visibleOptions.length > 0 && visibleSelectedCount === visibleOptions.length

  const handleToggleAll = (event: CheckboxChangeEvent) => {
    const visibleIds = visibleOptions.map((option) => option.id)
    if (!event.target.checked) {
      const dropped = new Set(visibleIds)
      onChange(selectedKeys.filter((key) => !dropped.has(key)))
      return
    }
    onChange([...selectedKeys, ...visibleIds.filter((id) => !selected.has(id))])
  }

  const handleToggle = (id: string) => (event: CheckboxChangeEvent) =>
    onChange(
      event.target.checked
        ? [...selectedKeys, id]
        : selectedKeys.filter((key) => key !== id)
    )

  const handleReset = () => {
    setSearch('')
    onChange([])
  }

  return (
    <HeaderFilterPanel>
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search companies…"
        aria-label="Search companies"
        allowClear
      />
      {visibleOptions.length === 0 ? (
        // The company list is a separate, slower request than the rows, so
        // "No matches" would be a lie for as long as it is in flight.
        <FilterEmptyText>
          {isLoading ? 'Loading companies…' : 'No matches'}
        </FilterEmptyText>
      ) : (
        <FilterOptionList>
          {visibleOptions.map((option) => (
            <Checkbox
              key={option.id}
              checked={selected.has(option.id)}
              onChange={handleToggle(option.id)}
            >
              {option.name}
            </Checkbox>
          ))}
        </FilterOptionList>
      )}
      <FilterPanelFooter>
        <Checkbox
          checked={areAllVisibleSelected}
          indeterminate={visibleSelectedCount > 0 && !areAllVisibleSelected}
          disabled={visibleOptions.length === 0 || isLoading}
          onChange={handleToggleAll}
        >
          Select all
        </Checkbox>
        <Button
          size="small"
          onClick={handleReset}
          disabled={selectedKeys.length === 0 && search.length === 0}
        >
          Reset
        </Button>
      </FilterPanelFooter>
    </HeaderFilterPanel>
  )
}

export { AccessFilterPanel, CompanyFilterPanel, StatusFilterPanel }
